import type { WindowSuggestion, WineDetail } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { acceptWindowSuggestion, askForWindow, ApiError } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { locale } from "../locale.js";
import { useFeatures } from "../session.js";
import { AnswerGrounds } from "./AnswerGrounds.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";
import { AiNotice } from "./AiNotice.js";
import { StatusLine } from "./StatusLine.js";

/**
 * Asking when this wine will be at its best.
 *
 * The decision is in `project-scope.md`, 2026-09-14: a window may be asked for
 * and is never written unasked. Everything about this component follows from
 * that one sentence.
 *
 * **Nothing happens until the button is pressed**, so the page is unchanged for
 * anybody who never presses it. **Nothing is saved until a year is taken**, one
 * field at a time. And a year that is edited before it is taken becomes the
 * owner's own — which the server decides by comparing, not by being told.
 *
 * The four suggested years arrive in fields rather than as text, because the
 * common case is not accepting a number but adjusting it: somebody reads
 * *2029–2040*, thinks it optimistic, and types 2036. What was proposed before
 * they changed it stays readable in the suggestion itself.
 */
const YEARS = ["drinkFrom", "peakFrom", "peakTo", "drinkBy"] as const;
type Year = (typeof YEARS)[number];

export function WindowHelp({
  cellarId,
  wineId,
  wine,
  onAccepted,
}: {
  cellarId: string;
  wineId: string;
  wine: WineDetail;
  onAccepted: () => void;
}) {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState<WindowSuggestion | null>(null);
  const [drafts, setDrafts] = useState<Record<Year, string>>({
    drinkFrom: "",
    peakFrom: "",
    peakTo: "",
    drinkBy: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Asking for a window costs a language model call and writing one is a
  // write, so a guest is offered neither. What a suggestion produced is on the
  // wine above either way: the years are the wine's, and `WindowReasons` shows
  // the argument that put them there to everybody who can read the page.
  const mayWrite = useMayWrite();
  // The administrator's switch, since 2026-09-28. Only this button goes: the
  // years and the reasons under them are the wine's, whoever asked for them.
  const { windowHelp } = useFeatures();

  const ask = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void askForWindow(cellarId, wineId)
      .then((answer) => {
        setSuggestion(answer);
        setDrafts({
          drinkFrom: answer.drinkFrom === null ? "" : String(answer.drinkFrom),
          peakFrom: answer.peakFrom === null ? "" : String(answer.peakFrom),
          peakTo: answer.peakTo === null ? "" : String(answer.peakTo),
          drinkBy: answer.drinkBy === null ? "" : String(answer.drinkBy),
        });
      })
      .catch((cause: unknown) => {
        // Each failure its own sentence. Switched off is not
        // broken, and a wine with nothing to count from is a question back
        // rather than a fault — telling somebody "something went wrong" when
        // the answer is "tell me the vintage" wastes the one thing they could
        // have done about it. A 403 is the administrator's switch thrown
        // while this page was open; the button goes on the next reload.
        const status = cause instanceof ApiError ? cause.status : 0;
        setError(
          t(
            status === 503
              ? "window.help.unavailable"
              : status === 403
                ? "features.switchedOff"
                : status === 422
                  ? "window.help.needsVintage"
                  : status === 0
                    ? "window.help.offline"
                    : "window.help.failed",
          ),
        );
      })
      .finally(() => setBusy(false));
  };

  const take = (only?: Year) => {
    if (suggestion === null || busy) return;
    const fields: Record<string, number | null> = {};
    for (const year of only === undefined ? YEARS : [only]) {
      const text = drafts[year].trim();
      if (text === "" && suggestion[year] === null) continue;
      fields[year] = text === "" ? null : Number(text);
    }
    if (Object.keys(fields).length === 0) return;

    setBusy(true);
    setError(null);
    void acceptWindowSuggestion(cellarId, wineId, suggestion.id, fields)
      .then(() => {
        setSuggestion(null);
        onAccepted();
      })
      .catch((cause: unknown) => {
        setError(
          t(
            cause instanceof ApiError && cause.status === 409
              ? "wines.windowBackwards"
              : "window.help.saveFailed",
          ),
        );
      })
      .finally(() => setBusy(false));
  };

  if (mayWrite !== true || !windowHelp) return null;

  if (suggestion === null) {
    return (
      <div className="mt-4" aria-busy={busy}>
        <button type="button" onClick={ask} disabled={busy} className={actionButton}>
          {busy ? t("window.help.asking") : t("window.help.ask")}
        </button>
        {/* How long it takes, said once, where the proposal will stand. The
            same line a screen reader is read when it changes — until this,
            fifteen seconds passed in silence and the years arrived in silence
            too. */}
        {busy && <StatusLine>{t("window.help.askingLong")}</StatusLine>}
        {error !== null && (
          <p role="alert" className="text-muted mt-2 max-w-prose text-sm">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <section className="border-line mt-4 border-t pt-4">
      {/* The arrival, announced. Short deliberately: the reason below it runs
          to several hundred characters and reading all of it aloud the moment
          it lands is not an announcement, it is an interruption. */}
      <StatusLine>{t("window.help.arrived")}</StatusLine>
      {/* The reason first and the years after it, because the reason is what is
          being judged. A number can only be believed or disbelieved; the
          sentence it came with says what it rests on, and that is the whole
          difference between a suggestion worth taking and one worth ignoring. */}
      <AiNotice className="mb-2" />
      <p className="max-w-prose text-sm">{suggestion.reason}</p>
      <AnswerGrounds
        labels={suggestion.basis.map((basis) => t(`window.basis.${basis}`))}
        told={suggestion.told}
      />

      {/* Aligned at the top, which is the one row in this interface that is.
          Everywhere else a row of controls ends in a button, and `items-end`
          is what puts that button's foot on the same line as the fields it
          belongs to. Here each column can carry a line *under* its box —
          what stands on the wine now — and only the years that have one do.
          Aligned at the bottom, a field with that line was pushed up by its
          height, so four labels that say the same kind of thing sat on two
          different lines. */}
      <div className="mt-4 flex flex-wrap items-start gap-4">
        {YEARS.map((year) => (
          <div key={year} className="w-32">
            <label className={fieldLabel} htmlFor={`suggested-${year}`}>
              {t(`wines.${year}`)}
            </label>
            <input
              id={`suggested-${year}`}
              value={drafts[year]}
              inputMode="numeric"
              onChange={(event) =>
                setDrafts((current) => ({ ...current, [year]: event.target.value }))
              }
              className={`${control} ${resting} mt-1`}
            />
            {/* What stands on the wine now, and **where it came from**. The
                suggestion does not replace it until it is taken, and seeing
                both is the point: 95 of these wines carry a year from the
                spreadsheet, and *the import said 2028* is a different thing to
                weigh against than *you said 2028*. */}
            {wine[year] !== null && (
              <p className="text-muted mt-1 text-xs">
                {t(`window.help.standing.${wine[`${year}Source`] ?? "unknown"}`, {
                  year: wine[year],
                })}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => take()}
          disabled={busy}
          className={actionButton}
        >
          {t("window.help.takeAll")}
        </button>
        <button
          type="button"
          onClick={() => setSuggestion(null)}
          disabled={busy}
          className="text-muted text-sm underline-offset-4 hover:underline"
        >
          {t("window.help.dismiss")}
        </button>
        <span className="text-muted text-xs">
          {t("window.help.provenance", {
            model: suggestion.model,
            date: new Date(suggestion.askedAt).toLocaleDateString(locale()),
          })}
        </span>
      </div>

      {error !== null && (
        <p role="alert" className="text-past mt-2 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}
